/* eslint-disable max-classes-per-file */
/* eslint-disable no-use-before-define */
import { Observable, merge, BehaviorSubject } from 'rxjs';
import { filter, share, switchMap } from 'rxjs/operators';
import { FRAME_NAME, sendMessageToSubFrame } from '@shell/utils/subFrames';
import { MessageSourceKey, SubscriptionLike, MessageSourceManager, PipeConfig } from '../interface';

export type WithCodeField<
    T extends Record<string, any> = Record<string, any>,
    C extends string | number = string | number
> = {
    code: C;
} & T;

/** 类似 Push 消息结构（有 `code` 字段即可）的 `Observable` 类型 */
export type PushMessageLikeObservable<T extends WithCodeField = WithCodeField> = Observable<T>;

const usePipeConfig = <K extends MessageSourceKey>(
    source$: PushMessageLikeObservable<any>,
    pipeConfig?: PipeConfig<K>,
) => {
    if (!pipeConfig) return source$;

    const shouldPipeKeys = Object.keys(pipeConfig) as K[];
    const piped$: PushMessageLikeObservable[] = shouldPipeKeys.map(key => {
        return pipeConfig[key]!(
            source$.pipe(
                filter(data => {
                    return String(data.code) === key;
                }),
            ),
        ).pipe(share());
    });
    const bypass$ = source$.pipe(
        filter(data => {
            return !shouldPipeKeys.includes(String(data.code) as K);
        }),
        share(),
    );

    return merge(...piped$, bypass$).pipe(share());
};

/**
 * 根 Push 消息源管理器
 */
export class RootPushSourceManager<K extends MessageSourceKey = MessageSourceKey>
    implements MessageSourceManager<K> {
    /** 消息源 */
    public sourceSubject$: BehaviorSubject<PushMessageLikeObservable>;

    public source$: PushMessageLikeObservable;

    /** 分发注册表 */
    private registry: Partial<Record<K, SubscriptionLike>>;

    constructor(source$?: PushMessageLikeObservable<any>, pipeConfig?: PipeConfig<K>) {
        // 没有给定消息源时，初始化一个空的消息源
        this.sourceSubject$ = new BehaviorSubject(source$ || new Observable());
        this.source$ = this.sourceSubject$.pipe(
            switchMap(source => usePipeConfig(source, pipeConfig)),
            share(),
        );

        this.registry = {};
    }

    public useSource(source$: PushMessageLikeObservable<any>) {
        this.sourceSubject$.next(source$);
    }

    public getSource<T = any>(key: K): PushMessageLikeObservable<WithCodeField<T, K>> {
        const newSource = this.source$.pipe(
            filter(data => {
                // 基于以下两点考虑，支持按 number 获取，但会统一转成 string 处理：
                // 1. 分发注册表和消息源缓存都使用了 object；而 JS 里 object 的 key 只能是 string
                // 2. 实际的 Push 消息中的 code 可能表示成 number，也可能表示成 string
                return String(data.code) === String(key);
            }),
            share(),
        );
        return newSource as PushMessageLikeObservable<WithCodeField<T, K>>;
    }

    public registerSource(key: K): SubscriptionLike {
        const subscription = this.registry[key] as SubscriptionLike | undefined;
        if (subscription) {
            return subscription;
        }

        const newSubscription = this.getSource(key).subscribe(message => {
            sendMessageToSubFrame(FRAME_NAME.HASH, {
                type: 'shell:push',
                key,
                data: message,
            });
        });
        this.registry[key] = newSubscription;
        return newSubscription;
    }

    public pipe(config?: PipeConfig<K>): MessageSourceManager<K> {
        return new PushSourceManager(this, this.source$, config);
    }
}

/**
 * `PushSourceManager` 也实现了 `MessageSourceManager`，但只对原始消息源进行加工操作，
 * 消息的生产和注册通过 `RootPushSourceManager` 进行。
 */
export class PushSourceManager<K extends MessageSourceKey = MessageSourceKey>
    implements MessageSourceManager<K> {
    private root: RootPushSourceManager<K>;

    /** 消息源 */
    private source$: PushMessageLikeObservable;

    /** 按 Key 缓存消息源，通过 `getSource` 按 Key 获取的消息源应当是同一个对象 */
    // private cache: Partial<Record<K, PushMessageLikeObservable>>;

    constructor(
        root: RootPushSourceManager<K>,
        source$: PushMessageLikeObservable,
        pipeConfig = {} as PipeConfig<K>,
    ) {
        this.root = root;
        // this.cache = {};
        this.source$ = usePipeConfig(source$, pipeConfig);
    }

    public getSource<T = any>(key: K): PushMessageLikeObservable<WithCodeField<T, K>> {
        const newSource = (this.source$ as PushMessageLikeObservable<WithCodeField<T, K>>).pipe(
            filter(data => {
                return String(data.code) === String(key);
            }),
            share(),
        );

        return newSource;
    }

    public registerSource(key: K) {
        return this.root.registerSource(key);
    }

    public pipe(config?: PipeConfig<K>) {
        return new PushSourceManager(this.root, this.source$, config);
    }
}
